import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Alert,
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    Paper,
    InputAdornment,
    Divider
} from '@mui/material';
import {
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Send as SendIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../config/db';
import { JOB_STORE } from '../../config/constants';
import useDictation from '../../hooks/useDictation';
import SelectSingleEmployeeModal from '../shared/SelectSingleEmployeeModal';
import SignatureModalWithAuto from '../common/SignatureModalWithAuto';
import unifiedFormService, { getByUUID } from '../../services/unifiedFormService';
import { TENANT_COMPANY_CODE_SHORT } from '../../config/tenant';

const TYPE = 'post_work_area_turnover';

export default function PostWorkAreaTurnoverForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [form, setForm] = useState({
        uuid: '',
        Job: '', // job code string (uppercase like in IndexedDB)
        jobName: '',
        workArea: '',
        workPerformed: '',
        dhgSupervisor: '',
        dhgSupervisorSignature: '',
        emailDate: dayjs().format('YYYY-MM-DD'),
        clientRepresentative: '',
        clientRepresentativeTitle: '',
        clientRepresentativeDate: dayjs().format('YYYY-MM-DD'),
        clientRepresentativeSignature: '',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [clientSignatureModalOpen, setClientSignatureModalOpen] = useState(false);
    const sigPadRef = useRef(null);
    const clientSigPadRef = useRef(null);

    // Dictation
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({ lang: 'en-US' });
    const [dictationTarget, setDictationTarget] = useState(null); // 'workArea' | 'workPerformed'

    // Sort jobs by Job number in descending order (highest to lowest)
    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA; // Descending order
        });
    }, [jobs]);

    // Load jobs from IndexedDB
    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();
                if (!db.objectStoreNames.contains(JOB_STORE)) return;
                const tx = db.transaction(JOB_STORE, 'readonly');
                const store = tx.objectStore(JOB_STORE);
                const allJobs = await store.getAll();
                const options = (allJobs || [])
                    .filter(j => j && (j.Job || j.JobName))
                    .map(j => ({ ...j, label: `${j.Job || ''} - ${j.JobName || ''}`.trim() }));
                setJobs(options);
            } catch (e) {
                // ignore
            }
        })();
    }, []);

    // Load existing draft by uuid if provided
    useEffect(() => {
        (async () => {
            const uuid = searchParams.get('uuid');
            if (!uuid) return;
            try {
                // Use the imported getByUUID function
                const rec = await getByUUID(uuid);
                if (rec) {
                    // The payload is stored directly in rec.payload
                    const savedData = rec.payload || {};

                    // Check if form is already submitted - only check IndexedDB status
                    if (rec.status === 'submitted') {
                        setSubmitted(true);
                    }
                    
                    setForm({
                        uuid: rec.uuid || uuid,
                        Job: savedData.Job || '',
                        jobName: savedData.jobName || savedData.JobName || '',
                        workArea: savedData.workArea || '',
                        workPerformed: savedData.workPerformed || '',
                        dhgSupervisor: savedData.dhgSupervisor || '',
                        dhgSupervisorSignature: savedData.dhgSupervisorSignature || '',
                        emailDate: savedData.emailDate || dayjs().format('YYYY-MM-DD'),
                        clientRepresentative: savedData.clientRepresentative || '',
                        clientRepresentativeTitle: savedData.clientRepresentativeTitle || '',
                        clientRepresentativeDate: savedData.clientRepresentativeDate || dayjs().format('YYYY-MM-DD'),
                        clientRepresentativeSignature: savedData.clientRepresentativeSignature || '',
                    });
                }
            } catch (e) {
                console.error('Error loading form:', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Autosave debounce
    const saveTimer = useRef(null);
    const queueSave = (data) => {
        // Don't autosave if form is already submitted
        if (submitted) return;
        
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                const saveData = {
                    ...data,
                    type: TYPE,
                    status: 'draft',
                    updatedAt: new Date().toISOString()
                };
                const { uuid } = await unifiedFormService.saveLocal(TYPE, saveData, data.uuid);
                // Ensure we keep saving into the same record (avoid generating new uuid each time)
                if (!data.uuid && uuid) {
                    setForm(prev => ({ ...prev, uuid }));
                }
            } catch (e) {
                console.error('Error saving form:', e);
            }
        }, 1000);
    };

    const updateField = (key, value) => {
        // Don't allow updates if form is already submitted
        if (submitted) return;
        
        const updated = { ...form, [key]: value };
        setForm(updated);
        queueSave(updated);
    };

    const validate = (data) => {
        const e = {};
        if (!data.Job) e.Job = 'Job is required';
        if (!data.workArea || data.workArea.trim().length === 0) e.workArea = 'Work area is required';
        if (!data.workPerformed || data.workPerformed.trim().length < 10) e.workPerformed = 'Min 10 characters';
        if (!data.dhgSupervisor) e.dhgSupervisor = `${TENANT_COMPANY_CODE_SHORT} Supervisor is required`;
        if (!data.dhgSupervisorSignature) e.dhgSupervisorSignature = 'Supervisor signature required';
        if (!data.emailDate) e.emailDate = 'Date is required';
        if (!data.clientRepresentative) e.clientRepresentative = 'Client Representative is required';
        if (!data.clientRepresentativeTitle) e.clientRepresentativeTitle = 'Client title is required';
        if (!data.clientRepresentativeDate) e.clientRepresentativeDate = 'Client date is required';
        if (!data.clientRepresentativeSignature) e.clientRepresentativeSignature = 'Client signature required';
        return e;
    };

    // Check if all required fields are filled
    const isFormValid = () => {
        const checks = {
            Job: !!form.Job,
            workArea: !!(form.workArea && form.workArea.trim().length > 0),
            workPerformed: !!(form.workPerformed && form.workPerformed.trim().length >= 10),
            dhgSupervisor: !!form.dhgSupervisor,
            dhgSupervisorSignature: !!form.dhgSupervisorSignature,
            emailDate: !!form.emailDate,
            clientRepresentative: !!form.clientRepresentative,
            clientRepresentativeTitle: !!form.clientRepresentativeTitle,
            clientRepresentativeDate: !!form.clientRepresentativeDate,
            clientRepresentativeSignature: !!form.clientRepresentativeSignature
        };
        
        const allValid = Object.values(checks).every(v => v === true);
        return allValid;
    };

    const handleSubmit = async () => {
        // Prevent resubmission if already submitted
        if (submitted) {
            setErrorMsg('This form has already been submitted');
            return;
        }
        
        setErrorMsg('');
        setSuccess('');
        const e = validate(form);
        setErrors(e);
        if (Object.keys(e).length > 0) return;
        setSaving(true);
        try {
            const payload = {
                uuid: form.uuid,
                // flattened top-level fields; handler also enriches by job code
                Job: form.Job,
                job: form.Job,
                jobName: form.jobName,
                workArea: form.workArea,
                workPerformed: form.workPerformed,
                dhgSupervisor: form.dhgSupervisor,
                dhgSupervisorSignature: form.dhgSupervisorSignature,
                emailDate: form.emailDate,
                clientRepresentative: form.clientRepresentative,
                clientRepresentativeTitle: form.clientRepresentativeTitle,
                clientRepresentativeDate: form.clientRepresentativeDate,
                clientRepresentativeSignature: form.clientRepresentativeSignature,
                // status handled by service
            };
            const res = await unifiedFormService.submit(TYPE, payload, form.uuid || undefined);
            // Fallback verification via local IndexedDB in case response parsing glitches
            let submittedOk = !!(res && res.success);
            try {
                if (!submittedOk) {
                    const localRec = await getByUUID(res?.uuid || form.uuid);
                    if (localRec && localRec.status === 'submitted') {
                        submittedOk = true;
                    }
                }
            } catch (_) {}

            if (submittedOk) {
                setSubmitted(true);
                setSuccess('Form submitted successfully! Redirecting...');
                if (res?.uuid) updateField('uuid', res.uuid);
                // Navigate to list after short delay to show success message
                setTimeout(() => {
                    navigate('/forms/post-work-area-turnover/list');
                }, 1500);
            } else if (res && res.queued) {
                setSubmitted(true);
                setSuccess('Offline: Form queued for submission when online');
                if (res?.uuid && !form.uuid) updateField('uuid', res.uuid);
                // Also navigate for queued forms
                setTimeout(() => {
                    navigate('/forms/post-work-area-turnover/list');
                }, 1500);
            } else {
                setErrorMsg('Submit failed');
            }
        } catch (e) {
            setErrorMsg('Submit failed');
        } finally {
            setSaving(false);
        }
    };

    const startFieldDictation = async (fieldKey) => {
        setDictationTarget(fieldKey);
        await startDictation({
            onResult: (text) => {
                if (!text) return;
                updateField(fieldKey, (form[fieldKey] ? form[fieldKey] + ' ' : '') + text);
            },
            onEnd: () => setDictationTarget(null),
            onError: () => setDictationTarget(null),
            continuous: false,
            interimResults: false,
            safetyTimeoutMs: 15000,
            prime: true,
        });
    };

    const stopFieldDictation = () => {
        try { stopDictation(); } catch (e) { /* ignore */ }
        setDictationTarget(null);
        setTimeout(() => { try { stopDictation(); } catch (e) {} }, 150);
    };

    // Ensure mic is released on unmount (prevents lingering mic usage on iOS)
    useEffect(() => {
        const kill = () => { try { stopDictation(); } catch (e) {} };
        window.addEventListener('pagehide', kill);
        document.addEventListener('visibilitychange', () => { if (document.hidden) kill(); });
        return () => {
            try { stopDictation(); } catch (e) { /* ignore */ }
            window.removeEventListener('pagehide', kill);
        };
    }, [stopDictation]);

    const onSelectSupervisor = (emp) => {
        const name = emp?.FullName || emp?.fullName || '';
        updateField('dhgSupervisor', name);
        setEmployeeModalOpen(false);
    };

    const acceptSignature = () => {
        if (sigPadRef.current) {
            const dataUrl = sigPadRef.current.toDataURL();
            // Check if canvas has any content (works for both draw and auto modes)
            const canvas = sigPadRef.current.getCanvas();
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const hasContent = imageData.data.some((pixel, i) => i % 4 === 3 && pixel > 0);

            if (hasContent) {
                updateField('dhgSupervisorSignature', dataUrl);
                setSignatureModalOpen(false);
            }
        }
    };

    const acceptClientSignature = () => {
        if (clientSigPadRef.current) {
            const dataUrl = clientSigPadRef.current.toDataURL();
            // Check if canvas has any content (works for both draw and auto modes)
            const canvas = clientSigPadRef.current.getCanvas();
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const hasContent = imageData.data.some((pixel, i) => i % 4 === 3 && pixel > 0);

            if (hasContent) {
                updateField('clientRepresentativeSignature', dataUrl);
                setClientSignatureModalOpen(false);
            }
        }
    };

    return (
        <Box sx={{ 
            height: 'calc(100vh - 60px)', // Account for fixed header
            backgroundColor: '#f5f5f5',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Paper sx={{ p: 2, borderRadius: 0, flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton onClick={() => navigate('/forms/post-work-area-turnover/list')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>Post-Work Area Turnover</Typography>
                    {submitted && (
                        <Chip 
                            label="Submitted" 
                            color="success" 
                            size="small" 
                            icon={<SendIcon />} 
                        />
                    )}
                </Stack>
            </Paper>

            {/* Main Content */}
            <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
                {submitted && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This form has been submitted and is now read-only. No further changes can be made.
                    </Alert>
                )}
                {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                {/* Main Form */}
                <Paper sx={{ p: 3, mb: 2 }}>
                    {/* Job and Work Area Section */}
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Job Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <JobSelector
                                jobs={sortedJobs}
                                jobCode={form.Job}
                                jobName={form.jobName}
                                onSelect={(val) => {
                                    // Don't allow changes if submitted
                                    if (submitted) return;

                                    if (!val) {
                                        updateField('Job', '');
                                        updateField('jobName', '');
                                        return;
                                    }
                                    // Job code is stored in 'Job' field from IndexedDB
                                    const code = val.Job || '';
                                    const name = val.JobName || '';

                                    // Update both fields at once to avoid re-renders
                                    setForm(prev => ({
                                        ...prev,
                                        Job: code,
                                        jobName: name
                                    }));

                                    // Queue save with updated data
                                    queueSave({
                                        ...form,
                                        Job: code,
                                        jobName: name
                                    });
                                }}
                                error={errors.Job}
                                disabled={submitted}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Work Area"
                                value={form.workArea}
                                onChange={(e) => updateField('workArea', e.target.value)}
                                size="small"
                                fullWidth
                                disabled={submitted}
                                required
                                error={!!errors.workArea}
                                helperText={errors.workArea}
                                InputProps={{
                                    endAdornment: speechSupported && !submitted && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (isListening && dictationTarget === 'workArea') stopFieldDictation();
                                                    else if (isListening && dictationTarget && dictationTarget !== 'workArea') {
                                                        stopFieldDictation();
                                                        setTimeout(() => startFieldDictation('workArea'), 140);
                                                    } else {
                                                        startFieldDictation('workArea');
                                                    }
                                                }}
                                                color={isListening && dictationTarget === 'workArea' ? "error" : "default"}
                                            >
                                                {isListening && dictationTarget === 'workArea' ? <MicOffIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Work Performed"
                                value={form.workPerformed}
                                onChange={(e) => updateField('workPerformed', e.target.value)}
                                size="small"
                                fullWidth
                                multiline
                                minRows={3}
                                required
                                error={!!errors.workPerformed}
                                helperText={errors.workPerformed || 'Describe the work performed (min 10 characters)'}
                                disabled={submitted}
                                InputProps={{
                                    endAdornment: speechSupported && !submitted && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (isListening && dictationTarget === 'workPerformed') stopFieldDictation();
                                                    else if (isListening && dictationTarget && dictationTarget !== 'workPerformed') {
                                                        stopFieldDictation();
                                                        setTimeout(() => startFieldDictation('workPerformed'), 140);
                                                    } else {
                                                        startFieldDictation('workPerformed');
                                                    }
                                                }}
                                                color={isListening && dictationTarget === 'workPerformed' ? "error" : "default"}
                                            >
                                                {isListening && dictationTarget === 'workPerformed' ? <MicOffIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    {/* Company Supervisor Section */}
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {TENANT_COMPANY_CODE_SHORT} Supervisor
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                        <TextField
                            size="small"
                            value={form.dhgSupervisor || ''}
                            label="Supervisor"
                            placeholder="Select supervisor"
                            disabled={submitted}
                            required
                            error={!!errors.dhgSupervisor}
                            helperText={errors.dhgSupervisorSignature ? ' ' : (errors.dhgSupervisor || '')}
                            sx={{ 
                                flex: 2,
                                '& .MuiInputBase-input': {
                                    cursor: submitted ? 'default' : 'pointer'
                                }
                            }}
                            onClick={() => !submitted && setEmployeeModalOpen(true)}
                            InputProps={{
                                readOnly: true,
                                endAdornment: !submitted && (
                                    <InputAdornment position="end">
                                        <PersonSearchIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <TextField
                            label="Date"
                            type="date"
                            value={form.emailDate}
                            onChange={(e) => updateField('emailDate', e.target.value)}
                            size="small"
                            sx={{ width: 180 }}
                            disabled={submitted}
                            required
                            error={!!errors.emailDate}
                            helperText={errors.emailDate}
                            InputLabelProps={{ shrink: true }}
                        />
                        <Box sx={{ width: 200 }}>
                            {form.dhgSupervisorSignature ? (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    height: 40
                                }}>
                                    <Box 
                                        sx={{ 
                                            border: '1px solid #e0e0e0', 
                                            borderRadius: 1, 
                                            p: 0.5,
                                            bgcolor: 'white',
                                            height: 40,
                                            width: 150,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                    >
                                        <img 
                                            src={form.dhgSupervisorSignature} 
                                            alt="Signature" 
                                            style={{ 
                                                maxHeight: 30, 
                                                maxWidth: '100%', 
                                                width: 'auto',
                                                height: 'auto',
                                                display: 'block',
                                                objectFit: 'contain'
                                            }} 
                                        />
                                        {!submitted && (
                                            <IconButton
                                                size="small"
                                                sx={{ 
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    bgcolor: 'error.main',
                                                    color: 'white',
                                                    width: 20,
                                                    height: 20,
                                                    '&:hover': {
                                                        bgcolor: 'error.dark'
                                                    }
                                                }}
                                                onClick={() => updateField('dhgSupervisorSignature', '')}
                                                title="Remove signature"
                                            >
                                                <ClearIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {!submitted && (
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setSignatureModalOpen(true)}
                                            title="Change signature"
                                        >
                                            <DrawIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            ) : (
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    startIcon={<DrawIcon />} 
                                    onClick={() => setSignatureModalOpen(true)}
                                    disabled={submitted}
                                    fullWidth
                                    sx={{ height: 40 }}
                                >
                                    Add Signature
                                </Button>
                            )}
                            {errors.dhgSupervisorSignature && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {errors.dhgSupervisorSignature}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    {/* Client Representative Section */}
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Client Representative
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Stack spacing={2} sx={{ flex: 2 }}>
                            <TextField
                                label="Name"
                                value={form.clientRepresentative}
                                onChange={(e) => updateField('clientRepresentative', e.target.value)}
                                size="small"
                                required
                                error={!!errors.clientRepresentative}
                                helperText={errors.clientRepresentativeSignature ? ' ' : (errors.clientRepresentative || '')}
                                disabled={submitted}
                                fullWidth
                            />
                            <TextField
                                label="Title"
                                value={form.clientRepresentativeTitle}
                                onChange={(e) => updateField('clientRepresentativeTitle', e.target.value)}
                                size="small"
                                disabled={submitted}
                                required
                                error={!!errors.clientRepresentativeTitle}
                                helperText={errors.clientRepresentativeTitle}
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            label="Date"
                            type="date"
                            value={form.clientRepresentativeDate}
                            onChange={(e) => updateField('clientRepresentativeDate', e.target.value)}
                            size="small"
                            disabled={submitted}
                            required
                            error={!!errors.clientRepresentativeDate}
                            helperText={errors.clientRepresentativeDate}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 180 }}
                        />
                        <Box sx={{ width: 200 }}>
                            {form.clientRepresentativeSignature ? (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    height: 40
                                }}>
                                    <Box 
                                        sx={{ 
                                            border: '1px solid #e0e0e0', 
                                            borderRadius: 1, 
                                            p: 0.5,
                                            bgcolor: 'white',
                                            height: 40,
                                            width: 150,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                    >
                                        <img 
                                            src={form.clientRepresentativeSignature} 
                                            alt="Client Signature" 
                                            style={{ 
                                                maxHeight: 30, 
                                                maxWidth: '100%', 
                                                width: 'auto',
                                                height: 'auto',
                                                display: 'block',
                                                objectFit: 'contain'
                                            }} 
                                        />
                                        {!submitted && (
                                            <IconButton
                                                size="small"
                                                sx={{ 
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    bgcolor: 'error.main',
                                                    color: 'white',
                                                    width: 20,
                                                    height: 20,
                                                    '&:hover': {
                                                        bgcolor: 'error.dark'
                                                    }
                                                }}
                                                onClick={() => updateField('clientRepresentativeSignature', '')}
                                                title="Remove signature"
                                            >
                                                <ClearIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {!submitted && (
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setClientSignatureModalOpen(true)}
                                            title="Change signature"
                                        >
                                            <DrawIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            ) : (
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    startIcon={<DrawIcon />} 
                                    onClick={() => setClientSignatureModalOpen(true)}
                                    disabled={submitted}
                                    fullWidth
                                    sx={{ height: 40 }}
                                >
                                    Client Signature *
                                </Button>
                            )}
                            {errors.clientRepresentativeSignature && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {errors.clientRepresentativeSignature}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Paper>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="large"
                        startIcon={submitted ? null : <SendIcon />} 
                        onClick={handleSubmit}
                        disabled={submitted || saving || !isFormValid()}
                    >
                        {saving ? 'Submitting...' : submitted ? 'Form Submitted' : !isFormValid() ? 'Fill All Required Fields' : 'Submit Form'}
                    </Button>
                </Box>
            </Box>

            {/* Modals */}
            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={onSelectSupervisor}
                title={`Select ${TENANT_COMPANY_CODE_SHORT} Supervisor`}
            />

            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.dhgSupervisor || 'Supervisor'}
                onAccept={acceptSignature}
                existingSignature={form.dhgSupervisorSignature}
            />

            <SignatureModalWithAuto
                open={clientSignatureModalOpen}
                onClose={() => setClientSignatureModalOpen(false)}
                sigPadRef={clientSigPadRef}
                employeeName={form.clientRepresentative || 'Client Representative'}
                onAccept={acceptClientSignature}
                existingSignature={form.clientRepresentativeSignature}
            />

            <Backdrop 
                open={saving} 
                sx={{ 
                    zIndex: (t) => t.zIndex.modal + 1, 
                    color: '#fff',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <CircularProgress color="inherit" size={60} />
                <Typography variant="h6">
                    Submitting form...
                </Typography>
            </Backdrop>
        </Box>
    );
}

// Job selector matching Incident style
function JobSelector({ jobs, jobCode, jobName, onSelect, error, disabled }) {
    const selected = useMemo(() => {
        if (!jobCode && !jobName) return null;
        
        // ALWAYS try to find by Job code first - it's the unique identifier
        if (jobCode && jobs.length > 0) {
            const found = jobs.find(j => {
                const code = j.Job || j.job || '';
                return code === jobCode || code === String(jobCode);
            });
            if (found) {
                return found;
            }
        }
        
        // If we have both code and name but didn't find by code, still show it
        // This handles cases where job exists but isn't in the current list
        if (jobCode && jobName) {
            const placeholder = { 
                Job: jobCode, 
                JobName: jobName, 
                label: `${jobCode} - ${jobName}`
            };
            return placeholder;
        }
        
        // Only if no code provided, try to find by name (less reliable)
        if (!jobCode && jobName && jobs.length > 0) {
            const found = jobs.find(j => {
                const name = j.JobName || j.jobName || '';
                return name === jobName;
            });
            if (found) {
                return found;
            }
        }
        
        return null;
    }, [jobs, jobCode, jobName])
    
    return (
        <Autocomplete
            size="small"
            options={jobs}
            value={selected}
            onChange={(e, newValue) => onSelect(newValue)}
            isOptionEqualToValue={(a, b) => {
                const aCode = a?.Job || a?.job || '';
                const bCode = b?.Job || b?.job || '';
                return aCode === bCode;
            }}
            getOptionLabel={(option) => {
                if (!option) return '';
                const code = option?.Job || option?.job || '';
                const name = option?.JobName || option?.jobName || '';
                return code && name ? `${code} - ${name}` : (code || name || '');
            }}
            filterOptions={(options, { inputValue }) => {
                const norm = (inputValue || '').toLowerCase();
                return options.filter(o => {
                    const code = (o.Job || o.job || '').toLowerCase();
                    const name = (o.JobName || o.jobName || '').toLowerCase();
                    return code.includes(norm) || name.includes(norm) || `${code} - ${name}`.includes(norm);
                });
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Select Job"
                    size="small"
                    error={!!error}
                    helperText={error || ''}
                />
            )}
            fullWidth
            disabled={disabled}
        />
    );
}
